import { RiInformationLine } from "@remixicon/react";
import type { LibrarySupervision } from "@screener/protocol";
import { Alert, AlertDescription, AlertTitle } from "@screener/uikit/components/alert";
import { Button } from "@screener/uikit/components/button";
import { Field, FieldError, FieldLabel } from "@screener/uikit/components/field";
import { Input } from "@screener/uikit/components/input";
import { LoadingRegion } from "@screener/uikit/components/loading-skeleton";
import { LockedState } from "@screener/uikit/components/locked-state";
import { SectionLabel } from "@screener/uikit/components/section-label";
import { SegmentedControl } from "@screener/uikit/components/segmented-control";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@screener/uikit/components/select";
import { Separator } from "@screener/uikit/components/separator";
import { Skeleton } from "@screener/uikit/components/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import { exchangeOptions } from "@/lib/exchange-options";
import { exchangeLabel, ROUND_GROUPS, SUPERVISIONS, supervisionLabel } from "@/lib/labels";
import { usePlanGate } from "@/lib/plan-gate-context";
import { draftDiffersFrom, type LibraryDraft } from "./library-filters";
import { LibraryRoundCut } from "./library-round-cut";
import { LibrarySummary } from "./library-summary";
import { type LibrarySnapshot, useLibrary } from "./use-library";

/**
 * Экран «Библиотека» — панель библиотеки сайзов §9 (spec `web-library-panel`,
 * design.md stage7-library-panel): фильтры §9, итог выборки и два разреза по
 * круглости. Запрос уходит по кнопке «Показать» или Enter, а не на каждое
 * нажатие клавиши.
 */

export interface LibraryScreenViewProps {
  snapshot: LibrarySnapshot;
  /** Биржи потока §7.6 — варианты фильтра по бирже. */
  exchanges: readonly string[];
  onDraftChange: (patch: Partial<LibraryDraft>) => void;
  onSubmit: () => void;
  onRetry: () => void;
}

/** Число KPI-карточек итога (`LibrarySummary`). */
const KPI_SKELETON_COUNT = 4;

/** Ошибка пары «от»/«до» — одна на оба поля, поэтому оба ссылаются на неё. */
const RANGE_ERROR_ID = "library-range-error";

/** Пояснение размера: подсказка у поля и строка статуса экрана (§116 п. 6). */
const PEAK_USD_HINT =
  "Размер — наибольший размер стены за её жизнь (peak_usd); обе границы включаются.";

/** Высота контролов тулбара по макету — 26 px. */
const CONTROL_HEIGHT = "h-[26px]";
const CONTROL_INPUT = `${CONTROL_HEIGHT} text-xs md:text-xs`;

/** Выбор «без фильтра по бирже»: подписи над полем в тулбаре нет, имя читается из значения. */
const ANY_EXCHANGE = "Любая биржа";

const SUPERVISION_OPTIONS = SUPERVISIONS.map((value) => ({
  value,
  label: supervisionLabel(value),
}));

interface FilterFormProps {
  snapshot: LibrarySnapshot;
  exchanges: readonly string[];
  loading: boolean;
  onDraftChange: (patch: Partial<LibraryDraft>) => void;
  onSubmit: () => void;
}

function FilterForm({ snapshot, exchanges, loading, onDraftChange, onSubmit }: FilterFormProps) {
  const { draft, errors, applied } = snapshot;
  // Биржа применённого фильтра остаётся в списке, даже если ушла из потока
  // (design.md, решение 4); так же — биржа, выбранная в черновике.
  const choices = exchangeOptions(
    exchangeOptions(exchanges, applied.exchange === "" ? null : applied.exchange),
    draft.exchange === "" ? null : draft.exchange,
  );
  const exchangeItems: Record<string, string> = {
    "": ANY_EXCHANGE,
    ...Object.fromEntries(choices.map((exchange) => [exchange, exchangeLabel(exchange)])),
  };

  return (
    <form
      aria-label="Фильтры библиотеки"
      className="@container/filters flex flex-col border-b"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="flex flex-wrap items-start gap-1.5 px-3 py-[5px]">
        <Field className="w-[150px]">
          <FieldLabel htmlFor="library-exchange" className="sr-only">
            Биржа
          </FieldLabel>
          <Select<string>
            items={exchangeItems}
            value={draft.exchange}
            onValueChange={(value) => onDraftChange({ exchange: value ?? "" })}
          >
            <SelectTrigger
              id="library-exchange"
              size="sm"
              className={`w-full ${CONTROL_HEIGHT} text-xs data-[size=sm]:h-[26px]`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">{ANY_EXCHANGE}</SelectItem>
              {choices.map((exchange) => (
                <SelectItem key={exchange} value={exchange}>
                  {exchangeLabel(exchange)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field className="w-[130px]">
          <FieldLabel htmlFor="library-base" className="sr-only">
            Монета
          </FieldLabel>
          <Input
            id="library-base"
            className={CONTROL_INPUT}
            value={draft.base}
            placeholder="Монета"
            autoComplete="off"
            onChange={(event) => onDraftChange({ base: event.target.value })}
          />
        </Field>
        {/* Разделители групп — только там, где все три группы помещаются в одну
            строку: при переносе разделитель оказался бы в конце строки. */}
        <Separator orientation="vertical" className="mx-1 hidden @[66rem]/filters:block" />
        {/* Подпись, поля «от»/«до» и подсказка — одна группа: на узком окне она
            переносится целиком, а не рассыпается за край. */}
        <div className="flex items-start gap-1.5">
          <SectionLabel className={`flex items-center ${CONTROL_HEIGHT}`}>Размер</SectionLabel>
          <Field className="w-[120px]" invalid={Boolean(errors.minUsd || errors.range)}>
            <FieldLabel htmlFor="library-min-usd" className="sr-only">
              Размер от, USD
            </FieldLabel>
            <Input
              id="library-min-usd"
              className={CONTROL_INPUT}
              aria-describedby={errors.range ? RANGE_ERROR_ID : undefined}
              inputMode="text"
              value={draft.minUsd}
              placeholder="от, USD"
              autoComplete="off"
              onChange={(event) => onDraftChange({ minUsd: event.target.value })}
            />
            {errors.minUsd ? <FieldError match>{errors.minUsd}</FieldError> : null}
          </Field>
          <span
            aria-hidden="true"
            className={`flex items-center ${CONTROL_HEIGHT} text-muted-foreground`}
          >
            −
          </span>
          <Field className="w-[120px]" invalid={Boolean(errors.maxUsd || errors.range)}>
            <FieldLabel htmlFor="library-max-usd" className="sr-only">
              Размер до, USD
            </FieldLabel>
            <Input
              id="library-max-usd"
              className={CONTROL_INPUT}
              aria-describedby={errors.range ? RANGE_ERROR_ID : undefined}
              inputMode="text"
              value={draft.maxUsd}
              placeholder="до, USD"
              autoComplete="off"
              onChange={(event) => onDraftChange({ maxUsd: event.target.value })}
            />
            {errors.maxUsd ? <FieldError match>{errors.maxUsd}</FieldError> : null}
          </Field>
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label="О размере"
                  className={`inline-flex ${CONTROL_HEIGHT} w-5 items-center justify-center rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring`}
                />
              }
            >
              <RiInformationLine aria-hidden="true" className="size-3.5" />
            </TooltipTrigger>
            <TooltipContent>{PEAK_USD_HINT}</TooltipContent>
          </Tooltip>
        </div>
        <Separator orientation="vertical" className="mx-1 hidden @[66rem]/filters:block" />
        <SegmentedControl<LibrarySupervision>
          label="Досмотренность"
          size="sm"
          options={SUPERVISION_OPTIONS}
          value={draft.supervision}
          onValueChange={(supervision) => onDraftChange({ supervision })}
        />
        <Button type="submit" size="sm" className={`${CONTROL_HEIGHT} text-xs`} disabled={loading}>
          Показать
        </Button>
      </div>
      {errors.range ? (
        <p id={RANGE_ERROR_ID} role="alert" className="px-3 pb-1.5 text-sm text-destructive">
          {errors.range}
        </p>
      ) : null}
    </form>
  );
}

/**
 * Скелетон результата: полоса из четырёх показателей, полоса долей и два
 * разреза по круглости — та же раскладка, что у `LibrarySummary` и
 * `LibraryRoundCut`, чтобы появление данных не сдвигало экран.
 */
function ResultSkeleton() {
  return (
    <LoadingRegion label="Считаем…" className="@container/library flex flex-col">
      <div className="grid grid-cols-2 @[48rem]/library:grid-cols-4">
        {Array.from({ length: KPI_SKELETON_COUNT }, (_, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: статичные декоративные ячейки
          <div key={index} className="flex flex-col gap-2 border-b px-4 py-3.5">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-[26px] w-32" />
            <Skeleton className="h-3 w-40 max-w-full" />
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-3 border-b px-4 py-3.5">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-2 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
      <div className="grid @[70rem]/library:grid-cols-2">
        {Array.from({ length: 2 }, (_, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: статичные декоративные таблицы
          <div key={index} className="flex min-w-0 flex-col gap-2 px-4 py-3">
            <Skeleton className="h-4 w-1/2" />
            {Array.from({ length: ROUND_GROUPS.length }, (_, row) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: статичные декоративные строки
              <Skeleton key={row} className="h-8 w-full" />
            ))}
          </div>
        ))}
      </div>
    </LoadingRegion>
  );
}

interface ResultProps {
  snapshot: LibrarySnapshot;
  onRetry: () => void;
}

function Result({ snapshot, onRetry }: ResultProps) {
  const { state } = snapshot;
  const planGate = usePlanGate();
  if (state.status === "idle" || state.status === "loading") {
    return <ResultSkeleton />;
  }
  if (state.status === "plan") {
    // Демон ответил `403 plan`: тариф библиотеку не даёт. Это замок, а не ошибка.
    return <LockedState title="Библиотека" text={planGate.mmApproachesText} />;
  }
  if (state.status === "unavailable") {
    return (
      <Alert variant="error">
        <div className="flex flex-col items-start gap-2">
          <div>
            <AlertTitle>Библиотека недоступна</AlertTitle>
            <AlertDescription>Выборку посчитать не удалось. Повторите запрос.</AlertDescription>
          </div>
          <Button variant="outline" size="sm" onClick={onRetry}>
            Повторить
          </Button>
        </div>
      </Alert>
    );
  }
  if (state.status === "invalid") {
    return (
      <Alert variant="error">
        <div>
          <AlertTitle>Фильтры не приняты</AlertTitle>
          <AlertDescription>
            Демон отверг эти фильтры. Проверьте поля и нажмите «Показать».
          </AlertDescription>
        </div>
      </Alert>
    );
  }
  const { response } = state;
  // Подходы выборки, у которых нет цены обнаружения, в разрез по обнаружению
  // не входят (§9 1.28): единственный расчёт клиента — разность счётчиков.
  const inDetectionCut = ROUND_GROUPS.reduce(
    (sum, group) => sum + response.round_at_detection.groups[group].approaches,
    0,
  );
  const missing = response.total.approaches - inDetectionCut;
  return (
    <div className="@container/library flex flex-col">
      {response.total.approaches === 0 ? (
        <p role="status" className="border-b px-4 py-2 text-sm text-muted-foreground">
          Под эти фильтры не подошёл ни один подход.
        </p>
      ) : null}
      <LibrarySummary aggregates={response.total} />
      {/* Оба разреза на экране сразу (§41 п. 2): рядом, когда области результата
          хватает на две таблицы, иначе один под другим. Порог — 70rem: замер
          в браузере, самая длинная подпись группы («круглая: половина порядка»)
          помещается рядом с числами при таблице не уже 548 px, то есть
          2 × 548 + gap-4 = 1112 px ≈ 69,5rem. Окно здесь не мерило — боковая
          панель отнимает 16rem. */}
      <div className="grid @[70rem]/library:grid-cols-2">
        <LibraryRoundCut cut={response.round_at_detection} missingApproaches={missing} />
        <div className="min-w-0 @[70rem]/library:border-l">
          <LibraryRoundCut cut={response.round_at_end} />
        </div>
      </div>
    </div>
  );
}

/** Презентационная часть экрана — по снимку контроллера, без обращения к сети. */
export function LibraryScreenView({
  snapshot,
  exchanges,
  onDraftChange,
  onSubmit,
  onRetry,
}: LibraryScreenViewProps) {
  const loading = snapshot.state.status === "loading";
  const dirty = draftDiffersFrom(snapshot.draft, snapshot.applied);
  return (
    <div data-slot="library-screen" className="flex min-h-full min-w-0 flex-col">
      <FilterForm
        snapshot={snapshot}
        exchanges={exchanges}
        loading={loading}
        onDraftChange={onDraftChange}
        onSubmit={onSubmit}
      />
      {dirty && snapshot.state.status !== "idle" ? (
        <p role="status" className="border-b px-4 py-1.5 text-sm text-warning">
          Фильтры изменены — нажмите «Показать».
        </p>
      ) : null}
      <div className="min-w-0 flex-1">
        <Result snapshot={snapshot} onRetry={onRetry} />
      </div>
      {/* Строка статуса — часть экрана, а не каркаса (design.md Д5): у каждого
          экрана свои подписи, общего компонента нет. */}
      <div
        data-slot="library-status-bar"
        className="flex min-h-6 shrink-0 items-center border-t px-4 py-[3px] text-[11px] leading-4 text-muted-foreground"
      >
        {PEAK_USD_HINT}
      </div>
    </div>
  );
}

export interface LibraryScreenProps {
  /** Экран виден (каркас не скрыл его атрибутом `hidden`). */
  active: boolean;
  /** Биржи потока §7.6 (design.md, решение 4). */
  exchanges: readonly string[];
  onSessionInvalid: () => void;
}

export function LibraryScreen({ active, exchanges, onSessionInvalid }: LibraryScreenProps) {
  const planGate = usePlanGate();
  // Тариф без библиотеки: запроса нет вовсе (экран не «активен» для контроллера).
  const library = useLibrary({ active: active && !planGate.libraryLocked, onSessionInvalid });
  if (planGate.libraryLocked) {
    return <LockedState title="Библиотека" text={planGate.mmApproachesText} />;
  }
  return (
    <LibraryScreenView
      snapshot={library.snapshot}
      exchanges={exchanges}
      onDraftChange={library.setDraft}
      onSubmit={library.submit}
      onRetry={library.retry}
    />
  );
}
